function getConfiguredApiBase(): string {
	const envBase =
		(typeof process !== "undefined"
			? process.env?.BUN_PUBLIC_API_BASE_URL
			: undefined) ?? "";
	const raw = envBase.trim();

	if (typeof window !== "undefined" && window.location?.hostname) {
		const hostname = window.location.hostname;
		if (
			hostname === "polygains.com" ||
			hostname === "www.polygains.com" ||
			hostname === "staging.polygains.com"
		) {
			return "https://api.polygains.com";
		}
		if (hostname === "localhost" || hostname === "127.0.0.1") {
			return "http://127.0.0.1:4069";
		}
	}

	if (!raw && typeof window !== "undefined" && window.location?.origin) {
		return window.location.origin;
	}
	if (!raw) throw new Error("Missing BUN_PUBLIC_API_BASE_URL");
	return /^https?:\/\//i.test(raw) ? raw : `http://${raw}`;
}

async function getJson<T>(
	pathname: string,
	query?: Record<string, string | undefined>,
): Promise<T> {
	const base = getConfiguredApiBase();
	const url = new URL(pathname, base.endsWith("/") ? base : `${base}/`);
	if (query) {
		for (const [key, value] of Object.entries(query)) {
			if (value === undefined) continue;
			url.searchParams.set(key, value);
		}
	}
	const response = await fetch(url.toString());
	if (!response.ok) {
		const body = await response.text().catch(() => "");
		throw new Error(
			`${response.status} ${response.statusText} ${body.slice(0, 200)}`,
		);
	}
	return (await response.json()) as T;
}

export interface SupersetChartMeta {
	id: number;
	title: string;
	description: string;
	vizType: string;
	supported: boolean;
}

export interface SupersetChartData {
	columns: string[];
	rows: Record<string, unknown>[];
	vizType: string;
	xField: string | null;
	seriesField: string | null;
	valueField: string | null;
}

export interface SupersetGuestToken {
	token: string;
	domain: string;
	dashboardUuid: string;
}

export async function fetchSupersetStatus(): Promise<{ configured: boolean }> {
	try {
		return await getJson<{ configured: boolean }>("/api/superset/status");
	} catch {
		return { configured: false };
	}
}

export async function fetchSupersetCharts(
	type: "market" | "user" = "market",
): Promise<SupersetChartMeta[]> {
	const payload = await getJson<{ data: SupersetChartMeta[] }>(
		"/api/superset/charts",
		{ type },
	);
	return payload.data;
}

const MAX_CONCURRENT_CHART_FETCHES = 8;
let activeChartFetches = 0;
const chartFetchQueue: (() => void)[] = [];

async function withChartFetchSlot<T>(task: () => Promise<T>): Promise<T> {
	if (activeChartFetches >= MAX_CONCURRENT_CHART_FETCHES) {
		await new Promise<void>((resolve) => chartFetchQueue.push(resolve));
	}
	activeChartFetches++;
	try {
		return await task();
	} finally {
		activeChartFetches--;
		chartFetchQueue.shift()?.();
	}
}

export interface ChartFilterOptions {
	marketId?: string | null;
	userAddress?: string | null;
}

export async function fetchSupersetChartData(
	chartId: number,
	filter?: string | null | ChartFilterOptions,
): Promise<SupersetChartData> {
	let marketId: string | undefined;
	let userAddress: string | undefined;

	if (typeof filter === "string") {
		marketId = filter;
	} else if (filter) {
		marketId = filter.marketId ?? undefined;
		userAddress = filter.userAddress ?? undefined;
	}

	return withChartFetchSlot(() =>
		getJson<SupersetChartData>(`/api/superset/chart/${chartId}/data`, {
			marketId,
			userAddress,
		}),
	);
}

export async function fetchSupersetGuestToken(
	type: "market" | "user" = "market",
): Promise<SupersetGuestToken> {
	return getJson<SupersetGuestToken>("/api/superset/guest-token", { type });
}
